<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue'
import { promptModal } from '@/composables/promptModal'
import { Teleport } from 'vue'

// Get state and actions from the composable
const { dialogState, handleConfirm, handleCancel } = promptModal()

// Local refs for elements if needed (e.g., for focus)
const modalRef = ref(null)
const inputRef = ref(null) // Ref for the input element

// --- Computed properties for clarity ---
const isConfirmMode = computed(() => dialogState.mode === 'confirm')
const isVisible = computed(() => dialogState.isVisible)

const confirmButtonText = computed(() => (isConfirmMode.value ? 'Ja' : 'OK'))
const cancelButtonText = computed(() => (isConfirmMode.value ? 'Nei' : 'Avbryt')) // Or 'Cancel'

// --- Event Handlers ---
function confirmAction() {
	// For prompt mode, pass the input value. For confirm, value is implicitly true.
	handleConfirm(isConfirmMode.value ? undefined : dialogState.inputValue)
}

function cancelAction() {
	// Only allow cancel via button in confirm mode
	handleCancel()
}

function onOverlayClick() {
	// Prevent closing via overlay click in confirm mode
	if (!isConfirmMode.value) {
		handleCancel() // Or maybe resolve with null? Depends on desired prompt behavior
	}
}

function handleKeyDown(e) {
	if (!isVisible.value) return // Don't process keys if not visible

	if (e.key === 'Escape') {
		// Prevent closing via Escape in confirm mode
		if (!isConfirmMode.value) {
			handleCancel()
		}
		// In confirm mode, Escape does nothing
	} else if (e.key === 'Enter') {
		// Enter confirms (Yes or OK)
		confirmAction()
	}
	// Could add ArrowLeft/Right or Y/N key handling for confirm mode if desired
}

// --- Lifecycle and Focus Management ---
onMounted(() => {
	document.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
	document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
	<Teleport to="body">
		<transition name="fade">
			<div v-if="isVisible" class="modal-overlay" @click.self="onOverlayClick">
				<div class="modal-container" ref="modalRef" role="dialog" aria-modal="true" :aria-labelledby="dialogState.title ? 'dialog-title' : null" :aria-describedby="dialogState.message ? 'dialog-message' : null">
					<div class="modal-header" v-if="dialogState.title">
						<h3 id="dialog-title">{{ dialogState.title }}</h3>
					</div>

					<div class="modal-body" v-if="dialogState.message || !isConfirmMode">
						<p id="dialog-message" v-if="dialogState.message">{{ dialogState.message }}</p>
						<!-- Conditionally render input for prompt mode -->
						<input v-if="!isConfirmMode" ref="inputRef" type="text" autocapitalize="off" autocorrect="off" spellcheck="false" v-model="dialogState.inputValue" class="modal-input" @keyup.enter="confirmAction" />
					</div>

					<div class="modal-footer">
						<!-- Cancel/No Button -->
						<button class="cancel-button" @click="cancelAction">{{ cancelButtonText }}</button>
						<!-- Confirm/Yes Button -->
						<button class="confirm-button" @click="confirmAction">{{ confirmButtonText }}</button>
					</div>
				</div>
			</div>
		</transition>
	</Teleport>
</template>

<style scoped>
/* Styles remain the same as provided */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.2);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	backdrop-filter: blur(5px);
}

.modal-container {
	width: 90%;
	max-width: 330px;
	background-color: white;
	border-radius: 13px;
	overflow: hidden;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
	animation: slide-up 0.3s ease-out;
}

.modal-header h3 {
	font-size: 18px;
	font-weight: 600;
	margin: 0;
	color: #333;
}

.modal-header {
	padding: 1rem;
	text-align: center;
}

.modal-body {
	padding: 1rem;
	padding-bottom: 1.25rem;
}

.modal-header + .modal-body {
	padding-top: 0;
}

.modal-body p {
	font-size: 1rem;
	color: #666;
	margin-bottom: 16px;
	text-align: center;
}

.modal-input {
	width: 100%;
	border: 1px solid #ddd;
	border-radius: 8px;
	padding: 12px 14px;
	font-size: 16px;
	background-color: #f5f5f7;
	color: #333;
	outline: none;
	transition: border-color 0.2s;
	box-sizing: border-box; /* Include padding/border in width */
}

.modal-input:focus {
	border-color: #007bff;
}

.modal-footer {
	display: flex;
	border-top: 1px solid #e6e6e6;
}

.modal-footer button {
	flex: 1;
	padding: 14px 20px;
	border: none;
	background: none;
	font-size: 16px;
	cursor: pointer;
	transition: background-color 0.2s;
	color: #007aff; /* iOS style blue text */
}

.cancel-button {
	border-right: 1px solid #e6e6e6;
	color: #007aff; /* iOS style blue text */
}

.confirm-button {
	font-weight: 600;
	color: #007aff; /* iOS style blue text */
}

.cancel-button + .confirm-button {
	border-left: 1px solid #e6e6e6;
}

.modal-footer button:hover {
	background-color: #f5f5f7;
}

.modal-footer button:active {
	background-color: #e6e6e6;
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

@keyframes slide-up {
	from {
		transform: translateY(20px);
		opacity: 0;
	}
	to {
		transform: translateY(0);
		opacity: 1;
	}
}
</style>
